import React from 'react';
import styled from 'styled-components';
import {
  screenSmMin,
  display1FontSizeXSmall,
  heading2FontSize,
  spacer8,
  spacer20,
  spacer40,
} from '@spotify-internal/encore-foundation';
import { Type } from '@spotify-internal/encore-web';
import { ContentfulAsset } from '../../../Contentful';
import { ContentfulImage } from '../../../Contentful/ContentfulImage';
import {
  DELAYS,
  Box,
  ImageAspectBox1x1,
  withAnimationContext,
} from '../../../ui';
import { ModuleType } from '../../index';
import { Colors } from './colorsType';
import { fadeInAnimation } from '../../../ui/Animations';

const HeroCaseStudyCover = styled(Box)`
  color: ${(props: Colors) => props.textColor};
  height: auto;
  &.tallest {
    height: 100%;
  }
  background-image: url("
    data:image/svg+xml,%3Csvg
    stroke-width='0.40'
    viewBox='100 20 757 501'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'%3E%3Cpath
    d='M0.999962 322.841L415.367 -9L337.576 217.561L484.861 125.057L385.807 502L865 226.049'
    stroke='%23${(props: Colors) =>
    props.textColor
      ? props.textColor.substring(1)
      : props.textColor}'/%3E%3C/svg%3E");
  background-position: center center;
  background-size: cover;
  background-repeat: no-repeat;
  background-color: ${(props: Colors) => props.backgroundColor};

  @media (max-width: ${screenSmMin}) {
    background-image: none;
    padding-bottom: ${spacer40};
  }
`;

const HeroCaseStudyWrapper = styled.div`
  padding-top: ${spacer20};
  display: flex;
  flex-direction: row;
  align-items: flex-end;
  flex-wrap: wrap;
  height: auto;
  align-self: center;
  text-align: ${(props: { centered: boolean }) =>
    props.centered ? 'center' : 'left'};
  @media (max-width: ${screenSmMin}) {
    padding-top: 0px;
    flex-direction: column;
    align-items: flex-start;
  }
`;

// TODO Remove hardcoding of font-size and line-height for mobile and web
const StyledHeadline = withAnimationContext(
  styled(Type.h1).attrs({
    variant: Type.heading1,
    weight: Type.bold,
  })`
    ${fadeInAnimation(DELAYS.SECOND)};
    font-size: ${display1FontSizeXSmall};
    line-height: 56px;
    height: 100%;
    padding-left: ${spacer40};
    padding-bottom: 0;
    align-self: flex-start;
    text-transform: none;

    @media (max-width: ${screenSmMin}) {
      width: 100%;
      padding-left: 0;
      font-size: ${heading2FontSize};
      line-height: 40px;
    }
  `,
);

const ImageWrapper = styled(ImageAspectBox1x1)`
  width: 35%;
  display: flex;
  align-items: center;

  @media (max-width: ${screenSmMin}) {
    width: 100%;
    margin-bottom: ${spacer8};
  }
`;

const StyledSupertext = withAnimationContext(
  styled(Type.p).attrs({
    variant: Type.body1,
    weight: Type.bold,
  })`
    ${fadeInAnimation(DELAYS.FIRST)};
    padding-left: ${spacer40};
    padding-bottom: ${spacer8};
    text-transform: uppercase;

    @media (max-width: ${screenSmMin}) {
      padding-top: ${spacer8};
      padding-left: 0;
      width: 100%;
    }
  `,
);

const Right = styled.div`
  width: 55%;
  display: flex;
  flex-direction: column;
  padding-left: ${spacer40};
  align-self: center;

  @media (max-width: ${screenSmMin}) {
    width: 100%;
    padding-left: 0px;
    padding-top: ${spacer8};
    justify-content: flex-start;
  }
`;

type HeroCaseStudyType = {
  supertext: string;
  headline: string;
  image?: ContentfulAsset;
  backgroundColor: string;
  textColor: string;
  ctaLink?: string;
  ctaText?: string;
};

export const HeroCaseStudy = React.forwardRef<
  React.MutableRefObject<ModuleType>,
  HeroCaseStudyType
>(
  (
    {
      supertext = '',
      headline = '',
      image,
      backgroundColor = '',
      textColor = '',
    },
    ref,
  ) => {
    return (
      <HeroCaseStudyCover
        backgroundColor={backgroundColor}
        textColor={textColor}
        ref={ref as React.RefObject<HTMLDivElement>}
      >
        <HeroCaseStudyWrapper centered={image === undefined}>
          {image && (
            <ImageWrapper>
              <ContentfulImage asset={image} />
            </ImageWrapper>
          )}
          <Right>
            <StyledSupertext>{supertext}</StyledSupertext>
            <StyledHeadline>{headline}</StyledHeadline>
          </Right>
        </HeroCaseStudyWrapper>
      </HeroCaseStudyCover>
    );
  },
);
